const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  getHeaders() {
    const token = localStorage.getItem('pfe_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  async request(method, endpoint, data = null) {
    const url = `${this.baseUrl}${endpoint}`;
    const options = {
      method,
      headers: this.getHeaders(),
    };

    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      options.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, options);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Network error' }));
        throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      if (response.status === 204) {
        return { success: true };
      }

      const text = await response.text();
      if (!text) {
        return { success: true };
      }

      const json = JSON.parse(text);
      // Extract data from nested responses like the original test-ui.html
      return json.data !== undefined ? json.data : json;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  get(endpoint) {
    return this.request('GET', endpoint);
  }

  post(endpoint, data) {
    return this.request('POST', endpoint, data);
  }

  put(endpoint, data) {
    return this.request('PUT', endpoint, data);
  }

  patch(endpoint, data) {
    return this.request('PATCH', endpoint, data);
  }

  delete(endpoint) {
    return this.request('DELETE', endpoint);
  }

  async upload(method, endpoint, formData) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = localStorage.getItem('pfe_token');
    const options = {
      method,
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    };

    try {
      const response = await fetch(url, options);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Network error' }));
        throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const text = await response.text();
      if (!text) {
        return { success: true };
      }

      const json = JSON.parse(text);
      return json.data !== undefined ? json.data : json;
    } catch (error) {
      console.error('API Upload Error:', error);
      throw error;
    }
  }

  async downloadBlob(endpoint) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = localStorage.getItem('pfe_token');
    const response = await fetch(url, {
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      let errorMessage = `File not found (HTTP ${response.status})`;
      try {
        const errorBody = await response.json();
        if (errorBody.message) {
          errorMessage = typeof errorBody.message === 'string'
            ? errorBody.message
            : JSON.stringify(errorBody.message);
        }
      } catch { /* response is not JSON, use default message */ }
      throw new Error(errorMessage);
    }

    return response.blob();
  }

  /** Trigger a browser download for a Blob. */
  saveBlob(blob, fileName) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Download the generated certificate of an attestation request.
   * Certificates are always referenced by request id — never by server path.
   */
  async downloadAttestation(requestId, fileName) {
    const blob = await this.downloadBlob(`/attestations/${requestId}/download`);
    const ext = blob.type.includes('wordprocessingml') ? '.docx' : '.pdf';
    const base = (fileName || `attestation_${requestId}`).replace(/\.(pdf|docx|txt)$/i, '');
    this.saveBlob(blob, `${base}${ext}`);
  }
}

const api = new ApiService();
export default api;
